import {
  KATALOG_POWIADOMIEN,
  kluczTrybu,
  MAKS_MINUT_SPOZNIENIA,
  NAZWY_ORGANOW_POWIADOMIEN,
  OPIS_TRYBU,
  organyPowiadomien,
  regulaEfektywna,
  trybOrganu,
  TRYBY_POWIADOMIEN,
  wierszeStartuTrybuWlasnego,
  wierszZdarzenia,
  zdarzeniaOrganu,
  type RegulaEfektywna,
  type TrybPowiadomien,
  type WierszRegulyPowiadomien,
  type ZdarzeniePowiadomienia,
} from '@insolitum/types';
import { AlertCircle, Bell, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// =============================================================================
// Karta „Powiadomienia e-mail" — kiedy organizacja dostaje maile z organów.
//
// Zgłoszenie właściciela z 01.11.2026: mail „Spóźnienie: 0 min" przy każdym
// zadaniu z minutnika, skrzynka zapchana. Każdy organ ma tu tryb do wyboru
// (Natychmiast / Zrównoważony / Tylko ważne / Własny); w trybie „Własny"
// każde zdarzenie ustawia się osobno.
//
// Skutek pokazujemy funkcją `regulaEfektywna()` — tą samą, którą nadawca
// (`neurosense-core` PocztaZdarzen) wymusza przed wysyłką, więc ekran nie może
// obiecać czegoś, czego nadawca nie zrobi. Zapis: `public.reguly_powiadomien`
// (migracja 889), RLS — zmienia właściciel i administrator (decyzja D3).
//
// `?organ=<organ>` rozwija i przewija do sekcji jednego organu — tak linkują
// tu ustawienia organów.
// Plan: docs/16-ZAMOWIENIA/realizacja/F0-reguly-powiadomien-organow.md
// =============================================================================

const POWTORKI: Array<{ wartosc: number | null; etykieta: string }> = [
  { wartosc: null, etykieta: 'domyślnie' },
  { wartosc: 60, etykieta: 'co godzinę' },
  { wartosc: 240, etykieta: 'co 4 godziny' },
  { wartosc: 1440, etykieta: 'raz na dobę' },
];

function opisSkutku(w: ZdarzeniePowiadomienia, r: RegulaEfektywna): string {
  if (!r.wlaczone) return 'nie wysyłamy';
  const czesci = ['wysyłamy'];
  if (w.warunek === 'od_minut_spoznienia' && r.odMinutSpoznienia > 0) {
    czesci.push(`od ${r.odMinutSpoznienia} min spóźnienia`);
  }
  if (w.warunek === 'tylko_krytyczne' && r.tylkoKrytyczne)
    czesci.push('tylko przekroczenia krytyczne');
  const powtorka = POWTORKI.find((p) => p.wartosc === r.powtorzCoMin);
  if (r.powtorzCoMin !== null)
    czesci.push(`powtórka ${powtorka?.etykieta ?? `co ${r.powtorzCoMin} min`}`);
  return czesci.join(', ');
}

type StanZapisu = {
  klucz: string;
  rodzaj: 'zapisuje' | 'zapisano' | 'blad';
  tekst?: string;
} | null;

export function KartaPowiadomien() {
  const { currentOrganization, impersonatingPartner } = useAuth();
  const [params] = useSearchParams();
  const orgId = currentOrganization?.organization_id ?? null;
  // Edycja = DOKŁADNIE to, co przepuszcza RLS (`is_org_admin`: aktywne
  // członkostwo owner/admin). NIE `hasRole()` — dla super-admina zwraca ono
  // `true` w każdej organizacji, także w podglądzie partnera (syntetyczne
  // członkostwo z rolą `partner`), a baza odrzuciłaby zapis 42501.
  const moznaZmieniac =
    !impersonatingPartner &&
    (currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin');
  const organWskazany = params.get('organ');

  const [wiersze, setWiersze] = useState<WierszRegulyPowiadomien[]>([]);
  const [laduje, setLaduje] = useState(true);
  const [bladOdczytu, setBladOdczytu] = useState<string | null>(null);
  const [zapis, setZapis] = useState<StanZapisu>(null);
  const [rozwiniete, setRozwiniete] = useState<Set<string>>(
    () => new Set(organWskazany ? [organWskazany] : [])
  );

  const organy = useMemo(() => organyPowiadomien(), []);

  /**
   * Organizacja, dla której ekran trzyma wiersze. Odpowiedź dla innej
   * (użytkownik przełączył organizację w trakcie zapytania) jest odrzucana —
   * inaczej „Własny" skopiowałby reguły poprzedniej organizacji, łącznie
   * z wyłączonymi alarmami krytycznymi, do bieżącej.
   */
  const orgBiezaca = useRef<string | null>(orgId);
  orgBiezaca.current = orgId;

  const wczytaj = useCallback(async () => {
    setWiersze([]);
    setZapis(null);
    if (!orgId) return;
    setLaduje(true);
    setBladOdczytu(null);
    const { data, error } = await supabase
      .from('reguly_powiadomien')
      .select('klucz, wlaczone, parametry')
      .eq('organization_id', orgId)
      .eq('kanal', 'email');
    if (orgBiezaca.current !== orgId) return;
    if (error) setBladOdczytu(error.message);
    else setWiersze((data ?? []) as WierszRegulyPowiadomien[]);
    setLaduje(false);
  }, [orgId]);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  useEffect(() => {
    if (!organWskazany || laduje) return;
    document.getElementById(`powiadomienia-${organWskazany}`)?.scrollIntoView({ block: 'start' });
  }, [organWskazany, laduje]);

  /** Zapisuje wiersze (upsert) i dopiero po sukcesie podmienia stan ekranu. */
  const zapisz = async (klucz: string, nowe: WierszRegulyPowiadomien[]) => {
    if (!orgId || !moznaZmieniac || laduje || bladOdczytu) return;
    setZapis({ klucz, rodzaj: 'zapisuje' });
    const { error } = await supabase.from('reguly_powiadomien').upsert(
      nowe.map((w) => ({
        organization_id: orgId,
        klucz: w.klucz,
        kanal: 'email',
        wlaczone: w.wlaczone,
        parametry: w.parametry,
      })),
      { onConflict: 'organization_id,klucz,kanal' }
    );
    if (orgBiezaca.current !== orgId) return;
    if (error) {
      setZapis({ klucz, rodzaj: 'blad', tekst: error.message });
      return;
    }
    setWiersze((stare) => {
      const klucze = new Set(nowe.map((w) => w.klucz));
      return [...stare.filter((w) => !klucze.has(w.klucz)), ...nowe];
    });
    setZapis({ klucz, rodzaj: 'zapisano' });
  };

  const zmienTryb = (organ: string, tryb: TrybPowiadomien) => {
    const wierszTrybu = { klucz: kluczTrybu(organ), wlaczone: true, parametry: { tryb } };
    // Przejście na „Własny" zaczyna od skutku poprzedniego trybu, nie od zera.
    const start =
      tryb === 'wlasny' && trybOrganu(organ, wiersze) !== 'wlasny'
        ? wierszeStartuTrybuWlasnego(organ, wiersze)
        : [];
    void zapisz(kluczTrybu(organ), [wierszTrybu, ...start]);
  };

  const zmienZdarzenie = (w: ZdarzeniePowiadomienia, zmiana: Partial<RegulaEfektywna>) => {
    const obecna = regulaEfektywna(w.zdarzenie, wiersze);
    const nowa = { ...obecna, ...zmiana };
    if (w.krytyczne && obecna.wlaczone && !nowa.wlaczone) {
      const zgoda = window.confirm(
        `„${w.nazwa}" to alarm krytyczny. Po wyłączeniu nikt w organizacji nie dostanie o nim maila. Wyłączyć?`
      );
      if (!zgoda) return;
    }
    void zapisz(w.zdarzenie, [wierszZdarzenia(w.zdarzenie, nowa)]);
  };

  const przelacz = (organ: string) =>
    setRozwiniete((s) => {
      const n = new Set(s);
      if (n.has(organ)) n.delete(organ);
      else n.add(organ);
      return n;
    });

  return (
    <div className="card" id="powiadomienia">
      <div className="card__head">
        <Bell className="w-5 h-5 text-aurora-brand" />
        <h2 className="card__title">Powiadomienia e-mail</h2>
      </div>
      <div className="card__body space-y-4">
        <p className="text-sm text-aurora-muted">
          Wybierz, kiedy organy wysyłają maile do Twojej organizacji. Alarmy krytyczne (HACCP,
          bezpieczeństwo, awarie) docierają w każdym trybie gotowym.
          {!moznaZmieniac && ' Zmienia je właściciel lub administrator organizacji.'}
        </p>

        {bladOdczytu && (
          <div className="flex items-center gap-2 text-sm text-red-400" role="alert">
            <AlertCircle className="w-4 h-4" />
            Nie udało się wczytać ustawień: {bladOdczytu}
          </div>
        )}

        {laduje ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 rounded-ids-md bg-aurora-bg-subtle animate-pulse" />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {organy.map((organ) => {
              const tryb = trybOrganu(organ, wiersze);
              const otwarty = rozwiniete.has(organ);
              const zdarzenia = zdarzeniaOrganu(organ);
              return (
                <li
                  key={organ}
                  id={`powiadomienia-${organ}`}
                  className="border border-aurora-border rounded-ids-md"
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                    aria-expanded={otwarty}
                    onClick={() => przelacz(organ)}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      {otwarty ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      {NAZWY_ORGANOW_POWIADOMIEN[organ] ?? organ}
                    </span>
                    <span className="text-sm text-aurora-muted">{OPIS_TRYBU[tryb].nazwa}</span>
                  </button>

                  {otwarty && (
                    <div className="px-4 pb-4 space-y-4">
                      <fieldset disabled={!moznaZmieniac}>
                        <legend className="field__label mb-2">Tryb</legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {TRYBY_POWIADOMIEN.map((t) => (
                            <label
                              key={t}
                              className={`p-3 border rounded-ids-md cursor-pointer ${
                                t === tryb ? 'border-aurora-brand' : 'border-aurora-border'
                              }`}
                            >
                              <span className="flex items-center gap-2 font-medium">
                                <input
                                  type="radio"
                                  name={`tryb-${organ}`}
                                  checked={t === tryb}
                                  disabled={!moznaZmieniac}
                                  onChange={() => zmienTryb(organ, t)}
                                />
                                {OPIS_TRYBU[t].nazwa}
                              </span>
                              <span className="block text-sm text-aurora-muted mt-1">
                                {OPIS_TRYBU[t].opis}
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <StatusZapisu zapis={zapis} klucz={kluczTrybu(organ)} />

                      <ul className="divide-y divide-aurora-border">
                        {zdarzenia.map((w) => (
                          <WierszZdarzenia
                            key={w.zdarzenie}
                            wpis={w}
                            regula={regulaEfektywna(w.zdarzenie, wiersze)}
                            edycja={tryb === 'wlasny' && moznaZmieniac}
                            zapis={zapis}
                            onZmiana={(z) => zmienZdarzenie(w, z)}
                          />
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Zdarzenia niekonfigurowalne istnieją, ale nie należą do organizacji. */}
        <p className="text-xs text-aurora-muted">
          Maile systemowe (program partnerski, kontrola licencji —{' '}
          {KATALOG_POWIADOMIEN.filter((w) => !w.konfigurowalne).length}) nie podlegają tym
          ustawieniom.
        </p>
      </div>
    </div>
  );
}

function StatusZapisu({ zapis, klucz }: { zapis: StanZapisu; klucz: string }) {
  if (!zapis || zapis.klucz !== klucz) return null;
  if (zapis.rodzaj === 'zapisuje') return <p className="text-xs text-aurora-muted">Zapisuję…</p>;
  if (zapis.rodzaj === 'zapisano') return <p className="text-xs text-green-400">Zapisano.</p>;
  return (
    <p className="text-xs text-red-400" role="alert">
      Nie zapisano: {zapis.tekst}
    </p>
  );
}

function WierszZdarzenia({
  wpis,
  regula,
  edycja,
  zapis,
  onZmiana,
}: {
  wpis: ZdarzeniePowiadomienia;
  regula: RegulaEfektywna;
  edycja: boolean;
  zapis: StanZapisu;
  onZmiana: (z: Partial<RegulaEfektywna>) => void;
}) {
  const [minuty, setMinuty] = useState(String(regula.odMinutSpoznienia));
  useEffect(() => setMinuty(String(regula.odMinutSpoznienia)), [regula.odMinutSpoznienia]);

  const zatwierdzMinuty = () => {
    const n = Math.round(Number(minuty));
    if (!Number.isFinite(n) || n < 0 || n > MAKS_MINUT_SPOZNIENIA) {
      setMinuty(String(regula.odMinutSpoznienia));
      return;
    }
    if (n !== regula.odMinutSpoznienia) onZmiana({ odMinutSpoznienia: n });
  };

  return (
    <li className="py-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {wpis.nazwa}
          {wpis.krytyczne && (
            <span className="badge" title="Alarm krytyczny — docierający w każdym trybie gotowym">
              <ShieldAlert className="w-3 h-3 inline mr-1" />
              krytyczne
            </span>
          )}
        </span>
        {edycja ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={regula.wlaczone}
              onChange={(e) => onZmiana({ wlaczone: e.target.checked })}
            />
            E-mail
          </label>
        ) : (
          <span className="text-sm text-aurora-muted">{opisSkutku(wpis, regula)}</span>
        )}
      </div>

      {edycja && regula.wlaczone && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-aurora-muted">
          {wpis.warunek === 'od_minut_spoznienia' && (
            <label className="flex items-center gap-2">
              Wyślij, gdy spóźnienie przekroczy
              <input
                className="input w-20"
                type="number"
                min={0}
                max={MAKS_MINUT_SPOZNIENIA}
                value={minuty}
                onChange={(e) => setMinuty(e.target.value)}
                onBlur={zatwierdzMinuty}
              />
              min
            </label>
          )}
          {wpis.warunek === 'tylko_krytyczne' && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={regula.tylkoKrytyczne}
                onChange={(e) => onZmiana({ tylkoKrytyczne: e.target.checked })}
              />
              tylko po przekroczeniu progu krytycznego
            </label>
          )}
          <label className="flex items-center gap-2">
            Powtórz najwyżej
            <select
              className="input w-auto"
              value={regula.powtorzCoMin === null ? '' : String(regula.powtorzCoMin)}
              onChange={(e) =>
                onZmiana({ powtorzCoMin: e.target.value === '' ? null : Number(e.target.value) })
              }
            >
              {POWTORKI.map((p) => (
                <option key={p.etykieta} value={p.wartosc === null ? '' : String(p.wartosc)}>
                  {p.etykieta}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <StatusZapisu zapis={zapis} klucz={wpis.zdarzenie} />
    </li>
  );
}
